<template>
  <div class="done_page">
    <p v-if="isLoading" class="done_loading">주문 정보를 불러오고 있습니다…</p>

    <!-- 주문 정보를 찾지 못한 경우 -->
    <section v-else-if="!orderInfo.oid" class="done_hero is-fail">
      <span class="done_hero_icon" aria-hidden="true">
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M12 8v4M12 16h.01" />
        </svg>
      </span>
      <h1>주문 정보를 찾을 수 없습니다</h1>
      <p>주문 내역은 마이페이지에서 다시 확인할 수 있어요.</p>
      <div class="done_actions">
        <router-link to="/mypage/list" class="done_btn done_btn--primary"
          >주문 내역 보기</router-link
        >
      </div>
    </section>

    <template v-else>
      <!-- 주문 상태 -->
      <section class="done_hero" :class="'is-' + statusView.tone">
        <span class="done_hero_icon" aria-hidden="true">
          <svg
            v-if="statusView.tone === 'success'"
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M5 12.5l4.5 4.5L19 7.5" />
          </svg>
          <svg
            v-else-if="statusView.tone === 'wait'"
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
          </svg>
          <svg
            v-else
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 8v4M12 16h.01" />
          </svg>
        </span>
        <h1>{{ statusView.title }}</h1>
        <p>{{ statusView.desc }}</p>

        <div class="done_order_no">
          <span>주문번호</span>
          <b>{{ orderInfo.oid }}</b>
          <button type="button" @click="copyText('oid', orderInfo.oid)">
            {{ copiedKey === "oid" ? "복사됨" : "복사" }}
          </button>
        </div>
      </section>

      <!-- 가상계좌 입금 안내 -->
      <section
        v-if="orderInfo.orderStatus === 'VBANK_WAIT' && vbank.number"
        class="done_card done_vbank"
        aria-labelledby="done-vbank-title"
      >
        <h2 id="done-vbank-title">입금 계좌</h2>
        <dl class="done_rows">
          <div>
            <dt>은행</dt>
            <dd>{{ vbank.bank }}</dd>
          </div>
          <div>
            <dt>계좌번호</dt>
            <dd class="done_account">
              <b>{{ vbank.number }}</b>
              <button
                type="button"
                class="done_chip_btn"
                @click="copyText('account', vbank.number)"
              >
                {{ copiedKey === "account" ? "복사됨" : "복사" }}
              </button>
            </dd>
          </div>
          <div v-if="vbank.holder">
            <dt>예금주</dt>
            <dd>{{ vbank.holder }}</dd>
          </div>
          <div>
            <dt>입금 금액</dt>
            <dd>
              <b>{{ won(orderInfo.totalPaidAmount) }}</b>
            </dd>
          </div>
          <div v-if="vbank.deadline">
            <dt>입금 기한</dt>
            <dd class="done_deadline">{{ vbank.deadline }} 까지</dd>
          </div>
        </dl>
        <p class="done_note">
          입금 기한 안에 정확한 금액을 입금해 주세요. 입금이 확인되면 주문이
          완료됩니다.
        </p>
      </section>

      <!-- 결제 정보 -->
      <section class="done_card" aria-labelledby="done-payment-title">
        <h2 id="done-payment-title">결제 정보</h2>
        <dl class="done_rows">
          <div class="done_total">
            <dt>결제 금액</dt>
            <dd>
              {{
                orderInfo.totalPaidAmount > 0
                  ? won(orderInfo.totalPaidAmount)
                  : "무료"
              }}
            </dd>
          </div>
          <div v-if="orderInfo.totalPaidAmount > 0 && payMethodLabel">
            <dt>결제수단</dt>
            <dd>{{ payMethodLabel }}</dd>
          </div>
          <template v-if="isCard">
            <div v-if="paymentInfo.card_Code || paymentInfo.card_Num">
              <dt>카드 정보</dt>
              <dd>
                {{ CARD_TYPE(paymentInfo.card_Code) }}
                {{ paymentInfo.card_Num }}
              </dd>
            </div>
          </template>
          <div
            v-if="(isCard || orderInfo.gopaymethod === 'DirectBank') && paymentInfo.applNum"
          >
            <dt>승인번호</dt>
            <dd>{{ paymentInfo.applNum }}</dd>
          </div>
          <div v-if="confirmDate">
            <dt>주문 일시</dt>
            <dd>{{ confirmDate }}</dd>
          </div>
        </dl>
      </section>

      <!-- 주문 상품 -->
      <section
        v-if="orderedItems.length > 0"
        class="done_card"
        aria-labelledby="done-items-title"
      >
        <h2 id="done-items-title">
          주문 상품 <span class="done_count">{{ orderedItems.length }}</span>
        </h2>
        <ul class="done_items">
          <li v-for="item in orderedItems" :key="item.key">
            <router-link :to="'/mall/view/' + item.itemId" class="done_item_thumb">
              <img
                v-if="item.thumbnailLink"
                :src="IMAGE_BASE + item.thumbnailLink"
                :alt="item.title"
              />
              <img v-else src="@/assets/img/null_img.jpg" :alt="item.title" />
            </router-link>
            <div class="done_item_info">
              <span class="done_item_badge">{{ item.kind }}</span>
              <router-link :to="'/mall/view/' + item.itemId" class="done_item_title">{{
                item.title
              }}</router-link>
              <p v-if="item.meta" class="done_item_meta">{{ item.meta }}</p>
            </div>
            <b v-if="item.price !== null" class="done_item_price">{{
              item.price
            }}</b>
          </li>
        </ul>
      </section>

      <!-- 주문자 정보 -->
      <section
        v-if="orderInfo.orderBuyer"
        class="done_card"
        aria-labelledby="done-buyer-title"
      >
        <h2 id="done-buyer-title">주문자 정보</h2>
        <dl class="done_rows">
          <div v-if="orderInfo.orderBuyer.buyerName">
            <dt>이름</dt>
            <dd>{{ orderInfo.orderBuyer.buyerName }}</dd>
          </div>
          <div v-if="orderInfo.orderBuyer.buyerTel">
            <dt>연락처</dt>
            <dd>{{ orderInfo.orderBuyer.buyerTel }}</dd>
          </div>
          <div v-if="orderInfo.orderBuyer.buyerEmail">
            <dt>이메일</dt>
            <dd>{{ orderInfo.orderBuyer.buyerEmail }}</dd>
          </div>
        </dl>
      </section>

      <!-- 다음 행동 -->
      <div class="done_actions">
        <router-link :to="primaryAction.to" class="done_btn done_btn--primary">{{
          primaryAction.label
        }}</router-link>
        <div class="done_actions_sub">
          <router-link
            v-for="action in secondaryActions"
            :key="action.to"
            :to="action.to"
            class="done_btn"
            >{{ action.label }}</router-link
          >
        </div>
      </div>
      <p
        v-if="orderInfo.orderStatus === 'ORDER_DONE' && orderedContents.length > 0"
        class="done_help"
      >
        구매한 콘텐츠는 마이페이지 &gt; 콘텐츠 보관함에서 내려받을 수 있습니다.
      </p>
    </template>
  </div>
</template>

<script setup>
import axios from "axios";
import { onMounted, onUnmounted, ref, computed } from "vue";
import { useRoute } from "vue-router";
import { inicisDateFormat, CARD_TYPE } from "@/assets/js/util";

const IMAGE_BASE = "https://eduwind-api.nodeblack.net/";

const route = useRoute();
const orderId = route.params.oid;

const isLoading = ref(true);
const orderInfo = ref({});

const won = (value) => `${Number(value || 0).toLocaleString("ko-KR")}원`;

const paymentInfo = computed(() => orderInfo.value.paymentInfo || {});
const isCard = computed(() =>
  ["Card", "VCard"].includes(orderInfo.value.gopaymethod),
);

const payMethodLabel = computed(
  () =>
    ({
      Card: "카드결제",
      VCard: "카드결제",
      VBank: "가상계좌",
      DirectBank: "실시간 계좌이체",
    })[orderInfo.value.gopaymethod] || "",
);

// 주문 상태별 안내 문구
const statusView = computed(() => {
  switch (orderInfo.value.orderStatus) {
    case "ORDER_DONE":
      return {
        tone: "success",
        title: "주문이 완료되었습니다",
        desc: "",
      };
    case "VBANK_WAIT":
      return {
        tone: "wait",
        title: "입금을 기다리고 있습니다",
        desc: "아래 계좌로 입금하시면 주문이 완료됩니다.",
      };
    case "PAY_WAIT":
      return {
        tone: "wait",
        title: "결제를 확인하고 있습니다",
        desc: "결제가 확인되면 주문이 완료돼요. 잠시 후 주문 내역을 확인해 주세요.",
      };
    case "PAY_FAIL":
      return {
        tone: "fail",
        title: "결제에 실패했습니다",
        desc: "결제가 완료되지 않았습니다. 다시 시도해 주세요.",
      };
    default:
      return {
        tone: "success",
        title: "주문이 접수되었습니다",
        desc: "주문 상태는 주문 상세에서 확인할 수 있습니다.",
      };
  }
});

// 가상계좌 정보 (PC와 모바일 결제의 필드 이름이 다름)
const vbank = computed(() => {
  const info = paymentInfo.value;
  const isPc = orderInfo.value.pay_device === "pc";
  const date = isPc ? info.vact_Date : info.VACT_Date;
  const time = isPc ? info.vact_Time : info.VACT_Time;
  return {
    bank: info.vactBankName,
    number: isPc ? info.vact_Num : info.VACT_Num,
    holder: info.vact_Name || info.VACT_Name,
    deadline: date && time ? inicisDateFormat(date, time) : "",
  };
});

const confirmDate = computed(() => {
  if (!orderInfo.value.confirm_date) return "";
  const date = new Date(orderInfo.value.confirm_date);
  if (Number.isNaN(date.getTime())) return orderInfo.value.confirm_date;
  return date.toLocaleString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
});

const orderedContents = computed(() => orderInfo.value.contentList || []);
const orderedProducts = computed(() => {
  const deliveryList = orderInfo.value.deliveryList;
  const warehouses = Array.isArray(deliveryList)
    ? deliveryList
    : Object.values(deliveryList || {});
  return warehouses
    .filter((warehouse) => Array.isArray(warehouse?.orderProductList))
    .flatMap((warehouse) => warehouse.orderProductList);
});

const itemPrice = (item, price) => {
  if (item.isFree === true) return "무료";
  return price > 0 ? won(price) : null;
};

const orderedItems = computed(() => [
  ...orderedContents.value.map((item) => ({
    key: "content-" + item.orderItemId,
    itemId: item.itemId,
    title: item.orderItemTitle || item.title,
    thumbnailLink: item.thumbnailLink,
    kind: "콘텐츠",
    meta: "",
    price: itemPrice(
      item,
      item.paidAmount ?? item.purchasePrice ?? item.regularPrice,
    ),
  })),
  ...orderedProducts.value.map((product) => ({
    key: "product-" + product.orderItemId,
    itemId: product.itemId,
    title: product.orderItemTitle || product.title,
    thumbnailLink: product.thumbnailLink,
    kind: "상품",
    meta: product.quantity ? `수량 ${product.quantity}개` : "",
    price: itemPrice(product, product.sellingPrice),
  })),
]);

// 다음 행동 버튼 (상황에 맞는 버튼을 강조)
const detailAction = computed(() => ({
  to: `/mypage/detail/${orderInfo.value.oid}`,
  label: "주문 상세 보기",
}));
const primaryAction = computed(() => {
  if (orderInfo.value.orderStatus === "PAY_FAIL") {
    return { to: "/mypage/cart", label: "장바구니로 돌아가기" };
  }
  if (
    orderInfo.value.orderStatus === "ORDER_DONE" &&
    orderedContents.value.length > 0
  ) {
    return { to: "/mypage/contentsBox", label: "콘텐츠 보관함으로" };
  }
  return detailAction.value;
});
const secondaryActions = computed(() =>
  [detailAction.value, { to: "/mall/hana", label: "하나큐브로 이동" }].filter(
    (action) => action.to !== primaryAction.value.to,
  ),
);

// 주문번호·계좌번호 복사
const copiedKey = ref("");
let copiedTimer = null;
const copyText = async (key, text) => {
  try {
    await navigator.clipboard.writeText(String(text));
    copiedKey.value = key;
    clearTimeout(copiedTimer);
    copiedTimer = setTimeout(() => {
      copiedKey.value = "";
    }, 1500);
  } catch (err) {
    console.error(err);
  }
};

const getOrderInfo = async () => {
  try {
    const url = `/new_api/customer/order/v2/order-result/${orderId}`;
    const { data, status } = await axios.get(url);
    if (status === 200 && data.code === "200") {
      orderInfo.value = data.data;
    } else {
      console.error(data);
    }
  } catch (err) {
    console.error(err);
  } finally {
    isLoading.value = false;
  }
};

onMounted(getOrderInfo);

onUnmounted(() => {
  clearTimeout(copiedTimer);
});
</script>

<style scoped>
.done_page {
  --done-navy: var(--main-navy, #0c3388);
  --done-navy-dark: #0a2a70;
  --done-ink: #1b1d21;
  --done-body: #3a3d44;
  --done-muted: #868e96;
  --done-line: #e9ecef;
  --done-soft: #f8f9fa;
  --done-tint: #eef2fb;
  --done-sale: #fa5252;
  --done-wait: #e8590c;

  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: 40rem;
  margin-left: auto;
  margin-right: auto;
  padding: 3rem 1.5rem 6rem;
  color: var(--done-ink);
}

.done_loading {
  padding: 6rem 0;
  color: var(--done-muted);
  text-align: center;
}

/* 주문 상태 */
.done_hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1rem 0 1.5rem;
  text-align: center;
}

.done_hero_icon {
  display: grid;
  place-items: center;
  width: 4rem;
  height: 4rem;
  margin-bottom: 1.25rem;
  border-radius: 9999px;
  background: var(--done-navy);
  color: #fff;
}

.done_hero.is-wait .done_hero_icon {
  background: #fff4e6;
  color: var(--done-wait);
}

.done_hero.is-fail .done_hero_icon {
  background: #fff0f0;
  color: var(--done-sale);
}

.done_hero h1 {
  font-size: 1.625rem;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.done_hero > p {
  margin-top: 0.5rem;
  color: var(--done-body);
  font-size: 0.9375rem;
  word-break: keep-all;
}

.done_order_no {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1.25rem;
  padding: 0.5rem 0.5rem 0.5rem 1rem;
  border-radius: 9999px;
  background: var(--done-soft);
  font-size: 0.875rem;
}

.done_order_no span {
  color: var(--done-muted);
}

.done_order_no b {
  font-weight: 600;
  word-break: break-all;
}

.done_order_no button,
.done_chip_btn {
  padding: 0.25rem 0.625rem;
  border: 1px solid #ced4da;
  border-radius: 9999px;
  background: #fff;
  color: var(--done-ink);
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}

/* 카드 */
.done_card {
  padding: 1.25rem 1.25rem 1rem;
  border: 1px solid var(--done-line);
  border-radius: 0.75rem;
  background: #fff;
}

.done_card h2 {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-bottom: 0.75rem;
  font-size: 1rem;
  font-weight: 700;
}

.done_count {
  min-width: 1.375rem;
  padding: 0 0.375rem;
  border-radius: 9999px;
  background: var(--done-tint);
  color: var(--done-navy);
  font-size: 0.75rem;
  line-height: 1.375rem;
  text-align: center;
}

.done_rows > div {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.5rem 0;
  font-size: 0.9375rem;
}

.done_rows dt {
  flex-shrink: 0;
  color: var(--done-muted);
}

.done_rows dd {
  color: var(--done-ink);
  text-align: right;
  word-break: break-all;
}

.done_total {
  margin-bottom: 0.25rem;
  padding-bottom: 0.875rem !important;
  border-bottom: 1px solid var(--done-line);
}

.done_total dt {
  color: var(--done-ink) !important;
  font-weight: 600;
}

.done_total dd {
  font-size: 1.375rem;
  font-weight: 800;
}

/* 가상계좌 */
.done_vbank {
  border-color: #ffd8a8;
  background: #fffaf3;
}

.done_account {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.done_account b {
  font-size: 1.0625rem;
  letter-spacing: 0.02em;
}

.done_deadline {
  color: var(--done-wait) !important;
  font-weight: 600;
}

.done_note {
  margin-top: 0.5rem;
  padding-top: 0.75rem;
  border-top: 1px dashed #ffd8a8;
  color: var(--done-body);
  font-size: 0.8125rem;
  word-break: keep-all;
}

/* 주문 상품 */
.done_items li {
  display: grid;
  grid-template-columns: 5.5rem minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.875rem;
  padding: 0.75rem 0;
}

.done_items li + li {
  border-top: 1px solid var(--done-line);
}

.done_item_thumb {
  display: block;
  aspect-ratio: 3 / 2;
  border: 1px solid var(--done-line);
  border-radius: 0.5rem;
  overflow: hidden;
}

.done_item_thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.done_item_info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.125rem;
  min-width: 0;
}

.done_item_badge {
  padding: 0.0625rem 0.375rem;
  border-radius: 0.25rem;
  background: var(--done-tint);
  color: var(--done-navy);
  font-size: 0.6875rem;
  font-weight: 700;
}

.done_item_title {
  display: -webkit-box;
  overflow: hidden;
  font-size: 0.9375rem;
  font-weight: 500;
  line-height: 1.4;
  word-break: keep-all;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.done_item_title:hover {
  text-decoration: underline;
}

.done_item_meta {
  color: var(--done-muted);
  font-size: 0.8125rem;
}

.done_item_price {
  font-size: 0.9375rem;
  font-weight: 700;
  white-space: nowrap;
}

/* 다음 행동 */
.done_actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
  margin-top: 0.5rem;
}

.done_actions_sub {
  display: flex;
  gap: 0.5rem;
}

.done_actions_sub .done_btn {
  flex: 1;
}

.done_btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 3rem;
  border: 1px solid #ced4da;
  border-radius: 0.5rem;
  background: #fff;
  color: var(--done-ink);
  font-size: 0.9375rem;
  font-weight: 600;
  transition: background-color 0.2s;
}

.done_btn:hover {
  background: var(--done-soft);
}

.done_btn--primary {
  height: 3.25rem;
  border-color: var(--done-navy);
  background: var(--done-navy);
  color: #fff;
  font-size: 1.0625rem;
}

.done_btn--primary:hover {
  background: var(--done-navy-dark);
}

.done_help {
  color: var(--done-muted);
  font-size: 0.8125rem;
  text-align: center;
  word-break: keep-all;
}

.done_hero.is-fail .done_actions {
  max-width: 20rem;
  margin-top: 1.5rem;
}

@media (max-width: 767px) {
  .done_page {
    padding: 2rem 1rem 4rem;
  }

  .done_hero h1 {
    font-size: 1.375rem;
  }

  .done_items li {
    grid-template-columns: 4.5rem minmax(0, 1fr);
  }

  .done_item_price {
    grid-column: 2;
  }
}
</style>
